import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import CompensationList from './List';
import CompensationCreate from './Create';
import CompensationDetail from './Detail';
import { injectIntl } from "react-intl";

const defaultFields = {
    Subject: { visible: false, label: '', required: false },
    CompensationType: { visible: false, label: '', required: false },
    Instructions: { visible: false, label: '', required: false },
    TaskOrder: { visible: false, label: '', required: false },
    ProjectOrder: { visible: false, label: '', required: false },
    EventAwareDate: { visible: false, label: '', required: false },
    Description: { visible: false, label: '', required: false },
    Assumption: { visible: false, label: '', required: false },
    EarlyWarning: { visible: false, label: '', required: false },
    InstructQuote: { visible: false, label: '', required: false }
}

const defaultReplyFields = {
    QuoteRequired: { visible: false, label: '', required: false },
    ReplyComment: { visible: false, label: '', required: false },
    ReplyAcceptedAssumption: { visible: false, label: '', required: false },
    InstructQuote: { visible: false, label: '', required: false }
}

///ALL FIELDS ARE DECIDED WITHIN THE getFields FUNCTION
///THE getFields FUNCTION IS CALLED WITHIN THE adjustContractTemplate and assign fields to workflowItem
export const getFields = (resources, placeholdersQA, workflowsQA, languageFile, wfid, globalResources, contract) => {
    const tempFields = { ...defaultFields };

    const workflowItem = workflowsQA[wfid];
    let clauses = workflowItem.clauses;
    
    // #region FIND CLAUSES
    if (!clauses) {
        clauses = placeholdersQA.CompensationClauses.values.filter(item => item.isActive === true);
        if (wfid === 37 || wfid === 67) {
            clauses = placeholdersQA.ClaimClauses.values.filter(item => item.isActive === true);
        }
        else if (wfid === 75) {
            clauses = placeholdersQA.ClientClaimWithNoticeClauses.values.filter(item => item.isActive === true);
        }
    }
    //#endregion

    tempFields.Subject = { visible: true, label: languageFile["GENERAL.FORMS.FIELD.SUBJECT"], required: true };

    if (resources.CompensationTypeLabel) {
        tempFields.CompensationType = { visible: true, label: resources.CompensationTypeLabel, required: true };
        tempFields.CompensationType.clauses = clauses;
    }

    const instructionTemplate = languageFile["CONTRACT.WF.COMPENSATION.FIELD.INSTRUCTION"];

    const instruction = instructionTemplate
        .replace("{PM_NAME}", globalResources.PM_NAME)
        .replace("{INSTRUCTION}", (workflowsQA['4'] || {}).notificationName);

    tempFields.Instructions = { visible: true, label: instruction, required: false };

    if (placeholdersQA.TaskOrderOptionEnabled) {
        const taskOrderOptionEnabled = placeholdersQA.TaskOrderOptionEnabled.values
            .find(phItem => phItem.field === 'TaskOrderOptionEnabled').isActive;

        if (taskOrderOptionEnabled) {
            let toLabel = languageFile["CONTRACT.WF.COMPENSATION.FIELD.TASK_ORDER"];

            try {
                toLabel = toLabel
                    .replace("{TASK_ORDER}", (workflowsQA['38']).notificationName);
            } catch (error) {
                console.log('Error loading Task Order notification name!');
            }

            tempFields.TaskOrder = { visible: true, label: toLabel, required: false };
        }
    }

    if (placeholdersQA.ProjectOrderOptionEnabled) {
        const projectOrderOptionEnabled = placeholdersQA.ProjectOrderOptionEnabled.values
            .find(phItem => phItem.field === 'ProjectOrderOptionEnabled').isActive;

        if (projectOrderOptionEnabled) {
            let poLabel = languageFile["CONTRACT.WF.COMPENSATION.FIELD.PROJECT_ORDER"];

            try {
                poLabel = poLabel
                    .replace("{PROJECT_ORDER}", (workflowsQA['72']).notificationName);
            } catch (error) {
                console.log('Error loading Project Order notification name!');
            }

            tempFields.ProjectOrder = { visible: true, label: poLabel, required: false };
        }
    }

    tempFields.EventAwareDate = { visible: wfid == 67 || wfid == 75, label: languageFile["CONTRACT.WF.COMPENSATION.AWARENESS_DATE"], required: true };
    tempFields.Description = { visible: true, label: languageFile["GENERAL.FORMS.FIELD.DETAILS"], required: true };

    tempFields.Assumption = { visible: wfid == 5, label: resources.PmAssumptionLabel, required: true };

    if (placeholdersQA.EWRelationEnabled) {
        const ewRelationEnabled = placeholdersQA.EWRelationEnabled.values
            .find(phItem => phItem.field === 'EWRelationEnabled').isActive;

        if (ewRelationEnabled) {
            let ewLabel = languageFile["CONTRACT.WF.COMPENSATION.LABEL.ASSOCIATED_EW"];
            try {
                ewLabel = ewLabel
                    .replace("{EARLY_WARNING}", (workflowsQA['1'] || workflowsQA['2'] || workflowsQA['74']).notificationName);
            } catch (error) {
                console.log('Error loading EW notification name!');
            }


            tempFields.EarlyWarning = { visible: true, label: ewLabel, required: false };
        }
    }

    if (wfid == 5 && workflowItem.quoteOptional) {
        tempFields.InstructQuote = { visible: true, label: languageFile["CONTRACT.WF.COMPENSATION.FIELD.INSTRUCT_QUOTE"], required: false };
    }

    const fields = { ...tempFields };

    //Final isActive check with related party placeholder
    let fieldsPH = placeholdersQA.Fields;
    if (wfid == 5 && placeholdersQA.FieldsPM) {
        fieldsPH = placeholdersQA.FieldsPM;
    }
    else if (wfid == 76 && placeholdersQA.FieldsClient) {
        fieldsPH = placeholdersQA.FieldsClient;
    }
    else if (wfid == 37 && placeholdersQA.FieldsCWQ) {
        fieldsPH = placeholdersQA.FieldsCWQ;
    }
    else if ((wfid == 67 || wfid == 75) && placeholdersQA.FieldsCWN) {
        fieldsPH = placeholdersQA.FieldsCWN;
    }

    //filtering assumption field because it only appears in PM mode
    if (wfid != 5 && fieldsPH?.values) {
        fieldsPH.values = fieldsPH.values?.filter(item => item.field !== 'Assumption');
    }

    fieldsPH?.values?.forEach(fieldItem => {
        if (fields[fieldItem.field]) {
            fields[fieldItem.field].label = fieldItem.isActive ? fieldItem.display : '';
            fields[fieldItem.field].visible = fieldItem.isActive;
        }
    });

    return fields;
}

///ALL REPLY FIELDS ARE DECIDED WITHIN THE getReplyFields FUNCTION
///THE getReplyFields FUNCTION IS CALLED WITHIN THE adjustContractTemplate and assign replyFields to workflowItem
export const getReplyFields = (resources, placeholdersQA, workflowsQA, languageFile, wfid) => {
    const replyFields = { ...defaultReplyFields };

    var replyOptions = null;

    // #region FIND REPLY OPTIONS
    if (wfid == 67 || wfid == 75) {
        if (placeholdersQA.ClaimWNAcceptanceOptions) {
            //if custom placeholder exist use placeholder
            replyOptions = placeholdersQA.ClaimWNAcceptanceOptions.values
                .filter(item => item.isActive === true)
                .map(x => ({ value: Number(x.field), display: x.display }));
        } else {
            //else use static options
            replyOptions = [
                { value: 1, display: languageFile["OPTIONS.STATUS.Accepted"] },
                { value: 0, display: languageFile["OPTIONS.STATUS.Not Accepted"] }
            ];
        }
    }
    else {
        if (placeholdersQA.CompensationEventsAcceptanceOptions) {
            //if custom placeholder exist use placeholder
            replyOptions = placeholdersQA.CompensationEventsAcceptanceOptions.values
                .filter(item => item.isActive === true)
                .map(x => ({ value: Number(x.field), display: x.display }));
        } else {
            //else use static options
            replyOptions = [
                { value: 1, display: languageFile["OPTIONS.STATUS.IS_COMPENSATION"] },
                { value: 0, display: languageFile["OPTIONS.STATUS.NOT_COMPENSATION"] }
            ];
        }
    }
    //#endregion

    replyFields.QuoteRequired = { visible: true, label: languageFile["GENERAL.FORMS.FIELD.STATUS"], required: false };
    replyFields.QuoteRequired.replyOptions = replyOptions;
    replyFields.ReplyComment = { visible: true, label: languageFile["REPORTS.COLUMNS.REPLYCOMMENT"], required: false };
    replyFields.ReplyAcceptedAssumption = { visible: true, label: resources.ReplyAcceptedAssumptionLabel, required: false };

    const workflowItem = workflowsQA[wfid];

    if (wfid == 7 && workflowItem.quoteOptional) {
        replyFields.InstructQuote = { visible: true, label: languageFile["CONTRACT.WF.COMPENSATION.FIELD.INSTRUCT_QUOTE"], required: false };
    }

    return replyFields;
}

class CompensationRouter extends React.Component {
    constructor(props) {
        super(props);
        this.notificationName = props.workflowItem.notificationName;
        this.abbreviation = props.workflowItem.abbreviation;
        this.replyPeriod = props.workflowItem.replyPeriod;

        this.notifyingParty = props.workflowItem.notifyingParty;
        this.replyingParty = props.workflowItem.replyingParty;


        if (props.mode === 'SUPPLIER') {
            this.documentType = 'Document612con';
        }
        if (props.mode === 'SHORT') {
            this.documentType = 'Document612scon';
        }
        else if (props.mode === 'CLAIM') {
            this.documentType = 'Document612CWQ';
        }
        else if (props.mode === 'PM') {
            this.documentType = 'Document611pm';
        }
        else if (props.mode === 'CLAIMWN') {
            this.documentType = 'Document611cwn';
        }
        else if (props.mode === 'CLIENTCWN') {
            this.documentType = 'Document611clientcwn';
        }
        else if (props.mode === 'CLIENT') {
            this.documentType = 'Document612client';
        }

        this.instructionNotificationName = (this.props.workflowsQA['4'] || {}).notificationName;

        // #region EW RELATION ENABLED
        this.ewRelationEnabled = true;
        try {
            if (props.placeholdersQA.EWRelationEnabled) {
                this.ewRelationEnabled = props.placeholdersQA.EWRelationEnabled.values
                    .find(phItem => phItem.field === 'EWRelationEnabled').isActive;
            }

        } catch (error) {
            console.log('EWRelationEnabled placeholder not available!');
            this.ewRelationEnabled = true;
        }
        //#endregion

        if (this.ewRelationEnabled) {
            try {
                this.ewNotificationName = (this.props.workflowsQA['1'] || this.props.workflowsQA['2'] || this.props.workflowsQA['74']).notificationName;
            } catch (error) {
                throw new Error('EWRelationEnabled but EW Workflows not found!');
            }
        }

        // #region TASK ORDER ENABLED
        this.taskOrderOptionEnabled = false;
        try {
            if (props.placeholdersQA.TaskOrderOptionEnabled) {
                this.taskOrderOptionEnabled = props.placeholdersQA.TaskOrderOptionEnabled.values
                    .find(phItem => phItem.field === 'TaskOrderOptionEnabled').isActive;
            }
        } catch (error) {
            console.log('TaskOrderOptionEnabled placeholder not available!');
            this.taskOrderOptionEnabled = false;
        }
        //#endregion

        if (this.taskOrderOptionEnabled) {
            this.taskOrderNotificationName = (this.props.workflowsQA['38'] || {}).notificationName;
        }

        // #region PROJECT ORDER ENABLED
        this.projectOrderOptionEnabled = false;
        try {
            if (props.placeholdersQA.ProjectOrderOptionEnabled) {
                this.projectOrderOptionEnabled = props.placeholdersQA.ProjectOrderOptionEnabled.values
                    .find(phItem => phItem.field === 'ProjectOrderOptionEnabled').isActive;
            }
        } catch (error) {
            console.log('ProjectOrderOptionEnabled placeholder not available!');
            this.projectOrderOptionEnabled = false;
        }
        //#endregion

        if (this.projectOrderOptionEnabled) {
            this.projectOrderNotificationName = (this.props.workflowsQA['72'] || {}).notificationName;
        }

        // #region FIELDS
        this.fields = { ...defaultFields };
        this.fields.Assumption = props.resources.PmAssumptionLabel;
        this.fields.Instructions = props.intl.formatMessage({ id: 'CONTRACT.WF.COMPENSATION.FIELD.INSTRUCTION' }, { PM_NAME: props.globalResources.PM_NAME, INSTRUCTION: this.instructionNotificationName });
        if (props.mode === 'CLAIMWN' || props.mode === 'CLIENTCWN') {
            this.fields.EventAwareDate = props.intl.formatMessage({ id: 'CONTRACT.WF.COMPENSATION.AWARENESS_DATE' });
        }

        try {
            //DEFAULT
            let fieldsPH = props.placeholdersQA.Fields;
            if (props.mode === 'PM' && props.placeholdersQA.FieldsPM) {
                //OVERRIDE IF EXIST
                fieldsPH = props.placeholdersQA.FieldsPM;
            }
            else if (props.mode === 'CLIENT' && props.placeholdersQA.FieldsClient) {
                //OVERRIDE IF EXIST
                fieldsPH = props.placeholdersQA.FieldsClient;
            }
            else if (props.mode === 'CLAIM' && props.placeholdersQA.FieldsCWQ) {
                //OVERRIDE IF EXIST
                fieldsPH = props.placeholdersQA.FieldsCWQ;
            }
            else if ((props.mode === 'CLAIMWN' || props.mode === 'CLIENTCWN') && props.placeholdersQA.FieldsCWN) {
                //OVERRIDE IF EXIST
                fieldsPH = props.placeholdersQA.FieldsCWN;
            }

            if (fieldsPH) {
                //IF FIELD PH EXIST USE IT
                fieldsPH.values.forEach(fieldItem => {
                    this.fields[fieldItem.field] = fieldItem.isActive ? fieldItem.display : '';
                });
            }
        } catch (error) {
            console.log('Error loading CEN Fields Placeholder!');
        }
        //#endregion
    }
    shouldComponentUpdate() {
        return false;
    }
    render() {
        const { workflowItem } = this.props;

        return (
            <Routes caseSensitive>
                <Route path={workflowItem.paths.basePath} exact element={<Navigate replace to={workflowItem.paths.defaultPath} />} />
                <Route
                    path={workflowItem.config.paths.list}
                    exact
                    element={<CompensationList
                        {...this.props}
                        fields={this.fields}
                        ewRelationEnabled={this.ewRelationEnabled}
                        ewNotificationName={this.ewNotificationName}
                        taskOrderOptionEnabled={this.taskOrderOptionEnabled}
                        taskOrderNotificationName={this.taskOrderNotificationName}
                        projectOrderOptionEnabled={this.projectOrderOptionEnabled}
                        projectOrderNotificationName={this.projectOrderNotificationName}
                        mode={this.props.mode}
                        title={this.notificationName}
                        abbreviation={this.abbreviation}
                        instructionNotificationName={this.instructionNotificationName}
                    />}

                />
                <Route
                    path={workflowItem.config.paths.create}
                    exact
                    element={
                        workflowItem.userFilters.mainNotice.createAllowed ? (
                            <CompensationCreate
                                {...this.props}
                                fields={this.fields}
                                ewRelationEnabled={this.ewRelationEnabled}
                                ewNotificationName={this.ewNotificationName}
                                taskOrderOptionEnabled={this.taskOrderOptionEnabled}
                                mode={this.props.mode}
                                instructionNotificationName={this.instructionNotificationName}
                                taskOrderNotificationName={this.taskOrderNotificationName}
                                projectOrderOptionEnabled={this.projectOrderOptionEnabled}
                                projectOrderNotificationName={this.projectOrderNotificationName}
                                title={this.notificationName}
                            />) : (
                            <Navigate replace to={workflowItem.paths.defaultPath} />
                        )
                    }
                />
                <Route
                    path={'/:notificationid/*'}
                    element={<CompensationDetail
                        {...this.props}
                        replyPeriod={this.replyPeriod}
                        fields={this.fields}
                        ewRelationEnabled={this.ewRelationEnabled}
                        ewNotificationName={this.ewNotificationName}
                        taskOrderOptionEnabled={this.taskOrderOptionEnabled}
                        mode={this.props.mode}
                        instructionNotificationName={this.instructionNotificationName}
                        taskOrderNotificationName={this.taskOrderNotificationName}
                        projectOrderOptionEnabled={this.projectOrderOptionEnabled}
                        projectOrderNotificationName={this.projectOrderNotificationName}
                        title={this.notificationName}
                        abbreviation={this.abbreviation}
                        documentType={this.documentType}
                        ewAbbreviation_SUP={this.ewAbbreviation_SUP}
                        ewAbbreviation_PM={this.ewAbbreviation_PM}
                        notifyUserFilter={workflowItem.notifyUserFilter}
                        canCreateInstruction={this.canCreateInstruction}
                        contractTemplate={this.props.contractTemplate}
                    />}
                />
            </Routes>
        );
    }
};



export default injectIntl(CompensationRouter);